import React, { useState, useEffect } from 'react';
import {
  Calendar, Plus, Edit, Trash2, CheckCircle, Lock, Copy,
  AlertCircle, RefreshCw
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import {
  useGetAnneesAcademiquesQuery,
  useGetAnneeAcademiqueQuery,
  useCreateAnneeAcademiqueMutation,
  useUpdateAnneeAcademiqueMutation,
  useDeleteAnneeAcademiqueMutation,
  useActiverAnneeMutation,
  useCloturerAnneeMutation,
  useCopierStructureMutation,
  useVerifierSchemaQuery,
  AnneeAcademiqueList,
  AnneeAcademiqueCreate,
} from '../../services/anneeAcademiqueApi';

export const AnneeAcademique: React.FC = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showCopyForm, setShowCopyForm] = useState(false);
  const [selectedAnneeId, setSelectedAnneeId] = useState<number | null>(null);
  const [formData, setFormData] = useState<AnneeAcademiqueCreate>({
    annee_debut: new Date().getFullYear(),
    annee_fin: new Date().getFullYear() + 1,
    date_debut_cours: null,
    date_fin_cours: null,
    date_debut_examens: null,
    date_fin_examens: null,
  });
  const [copyData, setCopyData] = useState({
    annee_debut: new Date().getFullYear() + 1,
    annee_fin: new Date().getFullYear() + 2,
  });
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // API Queries
  const { data: annees, isLoading, error, refetch } = useGetAnneesAcademiquesQuery();
  const { data: selectedAnnee } = useGetAnneeAcademiqueQuery(selectedAnneeId!, {
    skip: !selectedAnneeId,
  });

  // Mutations
  const [createAnnee, { isLoading: isCreating }] = useCreateAnneeAcademiqueMutation();
  const [updateAnnee, { isLoading: isUpdating }] = useUpdateAnneeAcademiqueMutation();
  const [deleteAnnee, { isLoading: isDeleting }] = useDeleteAnneeAcademiqueMutation();
  const [activerAnnee, { isLoading: isActivating }] = useActiverAnneeMutation();
  const [cloturerAnnee, { isLoading: isCloturing }] = useCloturerAnneeMutation();
  const [copierStructure, { isLoading: isCopying }] = useCopierStructureMutation();
  const { data: schemaInfo } = useVerifierSchemaQuery();

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  // Mettre à jour le formulaire quand on charge les détails de l'année sélectionnée
  useEffect(() => {
    if (selectedAnnee && showCreateForm) {
      setFormData({
        annee_debut: selectedAnnee.annee_debut,
        annee_fin: selectedAnnee.annee_fin,
        date_debut_cours: selectedAnnee.date_debut_cours || null,
        date_fin_cours: selectedAnnee.date_fin_cours || null,
        date_debut_examens: selectedAnnee.date_debut_examens || null,
        date_fin_examens: selectedAnnee.date_fin_examens || null,
      });
    }
  }, [selectedAnnee, showCreateForm]);

  const handleCreateAnnee = async () => {
    try {
      await createAnnee(formData).unwrap();
      showNotification('success', 'Année académique créée avec succès');
      setShowCreateForm(false);
      resetForm();
    } catch (err: any) {
      showNotification('error', err.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateAnnee = async () => {
    if (!selectedAnneeId) return;
    try {
      await updateAnnee({ id: selectedAnneeId, data: formData }).unwrap();
      showNotification('success', 'Année académique modifiée avec succès');
      setShowCreateForm(false);
      resetForm();
    } catch (err: any) {
      showNotification('error', err.data?.detail || 'Erreur lors de la modification');
    }
  };

  const handleDeleteAnnee = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette année académique ?')) return;
    try {
      await deleteAnnee(id).unwrap();
      showNotification('success', 'Année académique supprimée');
    } catch (err: any) {
      showNotification('error', err.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleActiverAnnee = async (id: number) => {
    try {
      await activerAnnee(id).unwrap();
      showNotification('success', 'Année académique activée');
    } catch (err: any) {
      showNotification('error', err.data?.detail || 'Erreur lors de l\'activation');
    }
  };

  const handleCloturerAnnee = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir clôturer cette année ? Cette action est irréversible.')) return;
    try {
      await cloturerAnnee(id).unwrap();
      showNotification('success', 'Année académique clôturée');
    } catch (err: any) {
      showNotification('error', err.data?.detail || 'Erreur lors de la clôture');
    }
  };

  const handleCopierStructure = async () => {
    if (!selectedAnneeId) return;
    try {
      const result = await copierStructure({
        id: selectedAnneeId,
        annee_debut: copyData.annee_debut,
        annee_fin: copyData.annee_fin,
      }).unwrap();
      showNotification('success', `Structure copiée: ${result.ues_copiees} UEs, ${result.matieres_copiees} matières`);
      setShowCopyForm(false);
    } catch (err: any) {
      showNotification('error', err.data?.detail || 'Erreur lors de la copie');
    }
  };

  const openEditForm = async (anneeId: number) => {
    setSelectedAnneeId(anneeId);
    setShowCreateForm(true);
    // Le formData sera mis à jour via useEffect quand selectedAnnee change
  };

  const openCopyForm = (anneeId: number) => {
    setSelectedAnneeId(anneeId);
    setCopyData({
      annee_debut: new Date().getFullYear() + 1,
      annee_fin: new Date().getFullYear() + 2,
    });
    setShowCopyForm(true);
  };

  const resetForm = () => {
    setSelectedAnneeId(null);
    setFormData({
      annee_debut: new Date().getFullYear(),
      annee_fin: new Date().getFullYear() + 1,
      date_debut_cours: null,
      date_fin_cours: null,
      date_debut_examens: null,
      date_fin_examens: null,
    });
  };

  const getStatutBadge = (annee: AnneeAcademiqueList) => {
    if (annee.est_cloturee) {
      return <Badge variant="default" className="flex items-center"><Lock size={12} className="mr-1" />Clôturée</Badge>;
    }
    if (annee.est_active) {
      return <Badge variant="success" className="flex items-center"><CheckCircle size={12} className="mr-1" />Active</Badge>;
    }
    return <Badge variant="warning">Brouillon</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle className="text-red-500 w-16 h-16 mb-4" />
        <p className="text-red-600 dark:text-red-400 mb-4">Erreur lors du chargement des années académiques</p>
        <Button onClick={() => refetch()} icon={<RefreshCw size={16} />}>
          Réessayer
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
          notification.type === 'success' ? 'bg-green-100 border-green-500 text-green-800 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300' : 'bg-red-100 border-red-500 text-red-800 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
        } border`}>
          <div className="flex items-center">
            {notification.type === 'success' ? <CheckCircle size={20} className="mr-2" /> : <AlertCircle size={20} className="mr-2" />}
            {notification.message}
          </div>
        </div>
      )}

      {/* Schema health */}
      {schemaInfo && (
        <div className={`rounded-lg border p-3 flex items-center gap-3 text-sm ${
          schemaInfo.coherent
            ? 'bg-green-50 dark:bg-green-900/15 border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/15 border-red-200 dark:border-red-800'
        }`}>
          <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${schemaInfo.coherent ? 'bg-green-500' : 'bg-red-500 animate-pulse'}`} />
          <div className="flex-1">
            <span className={`font-medium ${schemaInfo.coherent ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
              {schemaInfo.message}
            </span>
            <span className="text-gray-500 dark:text-gray-400 ml-3 text-xs">
              Schema: {schemaInfo.schema_actif} | Étudiants: {schemaInfo.compteurs?.etudiants ?? '?'} | Matières: {schemaInfo.compteurs?.matieres ?? '?'} | Copies: {schemaInfo.compteurs?.copies_preexamen ?? '?'}
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Gestion des Années Académiques</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm">Configuration et suivi des années académiques</p>
        </div>
        <Button variant="primary" icon={<Plus size={20} />} onClick={() => {
          resetForm();
          setShowCreateForm(true);
        }}>
          Nouvelle Année
        </Button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
          <div className="flex items-center">
            <Calendar className="text-blue-600 dark:text-blue-400 mr-3" size={24} />
            <div>
              <p className="text-sm text-blue-600 dark:text-blue-400">Total Années</p>
              <p className="text-2xl font-bold text-blue-900 dark:text-blue-200">{annees?.length || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-700">
          <div className="flex items-center">
            <CheckCircle className="text-green-600 dark:text-green-400 mr-3" size={24} />
            <div>
              <p className="text-sm text-green-600 dark:text-green-400">Année Active</p>
              <p className="text-2xl font-bold text-green-900 dark:text-green-200">
                {annees?.find(a => a.est_active)?.libelle || 'Aucune'}
              </p>
            </div>
          </div>
        </Card>
        <Card className="bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700">
          <div className="flex items-center">
            <Lock className="text-gray-600 dark:text-gray-400 mr-3" size={24} />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Années Clôturées</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {annees?.filter(a => a.est_cloturee).length || 0}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Liste des années académiques */}
      <div className="space-y-4">
        {annees && annees.length > 0 ? (
          annees.map((annee) => (
            <Card key={annee.id} className="overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      annee.est_active ? 'bg-green-100 dark:bg-green-900/30' : annee.est_cloturee ? 'bg-gray-100 dark:bg-gray-700' : 'bg-yellow-100 dark:bg-yellow-900/30'
                    }`}>
                      <Calendar className={`${
                        annee.est_active ? 'text-green-600' : annee.est_cloturee ? 'text-gray-600' : 'text-yellow-600'
                      }`} size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{annee.libelle}</h3>
                      <div className="flex items-center space-x-3 mt-1">
                        {getStatutBadge(annee)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {/* Bouton Copier structure */}
                    {!annee.est_cloturee && (
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<Copy size={16} />}
                        onClick={() => openCopyForm(annee.id)}
                        disabled={isCopying}
                      >
                        Copier structure
                      </Button>
                    )}

                    {/* Bouton Activer */}
                    {!annee.est_active && !annee.est_cloturee && (
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<CheckCircle size={16} />}
                        onClick={() => handleActiverAnnee(annee.id)}
                        disabled={isActivating}
                      >
                        {isActivating ? 'Activation...' : 'Activer'}
                      </Button>
                    )}

                    {/* Bouton Clôturer */}
                    {annee.est_active && (
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<Lock size={16} />}
                        onClick={() => handleCloturerAnnee(annee.id)}
                        disabled={isCloturing}
                        className="text-orange-600 border-orange-300 hover:bg-orange-50"
                      >
                        {isCloturing ? 'Clôture...' : 'Clôturer'}
                      </Button>
                    )}

                    {/* Bouton Modifier */}
                    {!annee.est_cloturee && (
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<Edit size={16} />}
                        onClick={() => openEditForm(annee.id)}
                      >
                        Modifier
                      </Button>
                    )}

                    {/* Bouton Supprimer */}
                    {!annee.est_active && !annee.est_cloturee && (
                      <Button
                        variant="danger"
                        size="sm"
                        icon={<Trash2 size={16} />}
                        onClick={() => handleDeleteAnnee(annee.id)}
                        disabled={isDeleting}
                      >
                        Supprimer
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))
        ) : (
          <Card>
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Aucune année académique</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">Commencez par créer une nouvelle année académique</p>
              <Button variant="primary" icon={<Plus size={16} />} onClick={() => setShowCreateForm(true)}>
                Créer une année
              </Button>
            </div>
          </Card>
        )}
      </div>

      {/* Formulaire de création/modification */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-6">
                {selectedAnneeId ? 'Modifier l\'Année Académique' : 'Créer une Nouvelle Année Académique'}
              </h3>

              <div className="space-y-6">
                {/* Années */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Année de début</label>
                    <Input
                      type="number"
                      value={formData.annee_debut}
                      onChange={(e) => setFormData({ ...formData, annee_debut: parseInt(e.target.value) })}
                      min={2020}
                      max={2100}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Année de fin</label>
                    <Input
                      type="number"
                      value={formData.annee_fin}
                      onChange={(e) => setFormData({ ...formData, annee_fin: parseInt(e.target.value) })}
                      min={2020}
                      max={2100}
                    />
                  </div>
                </div>

                <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  Libellé généré automatiquement: <strong>{formData.annee_debut}-{formData.annee_fin}</strong>
                </div>

                {/* Dates des cours */}
                <div>
                  <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">Période des cours</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Date de début</label>
                      <Input
                        type="date"
                        value={formData.date_debut_cours || ''}
                        onChange={(e) => setFormData({ ...formData, date_debut_cours: e.target.value || null })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Date de fin</label>
                      <Input
                        type="date"
                        value={formData.date_fin_cours || ''}
                        onChange={(e) => setFormData({ ...formData, date_fin_cours: e.target.value || null })}
                      />
                    </div>
                  </div>
                </div>

                {/* Dates des examens */}
                <div>
                  <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">Période des examens</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Date de début</label>
                      <Input
                        type="date"
                        value={formData.date_debut_examens || ''}
                        onChange={(e) => setFormData({ ...formData, date_debut_examens: e.target.value || null })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Date de fin</label>
                      <Input
                        type="date"
                        value={formData.date_fin_examens || ''}
                        onChange={(e) => setFormData({ ...formData, date_fin_examens: e.target.value || null })}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-8 pt-4 border-t">
                <Button variant="outline" onClick={() => {
                  setShowCreateForm(false);
                  resetForm();
                }}>
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  onClick={selectedAnneeId ? handleUpdateAnnee : handleCreateAnnee}
                  disabled={isCreating || isUpdating}
                >
                  {isCreating || isUpdating ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      {selectedAnneeId ? 'Modification...' : 'Création...'}
                    </>
                  ) : (
                    selectedAnneeId ? 'Modifier' : 'Créer'
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Formulaire de copie de structure */}
      {showCopyForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Copier la Structure</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
                Copiez les UEs et matières vers une nouvelle année académique. Les étudiants et les notes ne seront pas copiés.
              </p>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nouvelle année - début</label>
                    <Input
                      type="number"
                      value={copyData.annee_debut}
                      onChange={(e) => setCopyData({ ...copyData, annee_debut: parseInt(e.target.value) })}
                      min={2020}
                      max={2100}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nouvelle année - fin</label>
                    <Input
                      type="number"
                      value={copyData.annee_fin}
                      onChange={(e) => setCopyData({ ...copyData, annee_fin: parseInt(e.target.value) })}
                      min={2020}
                      max={2100}
                    />
                  </div>
                </div>

                <div className="text-sm text-gray-500 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                  <strong>Destination:</strong> {copyData.annee_debut}-{copyData.annee_fin}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <Button variant="outline" onClick={() => setShowCopyForm(false)}>
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCopierStructure}
                  disabled={isCopying}
                  icon={<Copy size={16} />}
                >
                  {isCopying ? 'Copie en cours...' : 'Copier'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Informations */}
      <Card className="bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
        <div className="flex items-start">
          <Calendar className="text-blue-600 dark:text-blue-400 mr-3 mt-1" size={20} />
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">Gestion des Années Académiques</h4>
            <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
              <li>• Une seule année académique peut être active à la fois</li>
              <li>• Les années clôturées ne peuvent plus être modifiées</li>
              <li>• Utilisez "Copier structure" pour dupliquer les UEs et matières vers une nouvelle année</li>
              <li>• Les étudiants et les notes ne sont jamais copiés entre les années</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AnneeAcademique;
